import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = "https://customer-assets.emergentagent.com/job_culinary-connect-37/artifacts/dowryrls_582627c1-c0bf-47f6-ab36-5cc8b78702e3.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-20 text-center">
        <div>
          <p className="font-serif text-2xl text-[#D4AF37] mb-4">Invalid Reset Link</p>
          <p className="text-[#A0A0A0] text-sm mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password"
            className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 text-sm hover:bg-[#D4AF37] hover:text-black transition-all">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = password.trim();
    if (trimmed !== confirm.trim()) { setError("Passwords do not match"); return; }
    if (trimmed.length < 8 || !/[A-Za-z]/.test(trimmed) || !/\d/.test(trimmed)) {
      setError("Password must be at least 8 characters and include both a letter and a number.");
      return;
    }
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-20 pb-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/"><img src={LOGO} alt="Logo" className="h-16 w-16 rounded-full mx-auto mb-4" /></Link>
          <h1 className="font-serif text-3xl text-white font-light">Set New Password</h1>
          <p className="text-[#A0A0A0] text-sm mt-2">Choose a strong new password for your account</p>
        </div>

        {success ? (
          <div className="text-center border border-emerald-800/40 bg-emerald-950/20 p-8" data-testid="reset-success-msg">
            <div className="text-emerald-400 text-3xl mb-4">✓</div>
            <h2 className="font-serif text-xl text-white mb-3">Password Updated!</h2>
            <p className="text-[#A0A0A0] text-sm">Redirecting you to login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">New Password</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="At least 8 characters" data-testid="reset-password-input"
                autoComplete="new-password"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white placeholder-white/25 text-sm outline-none transition-colors"
              />
              <p className="text-xs text-[#A0A0A0] mt-2">Minimum 8 characters, including at least one letter and one number.</p>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Confirm Password</label>
              <input
                type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                required placeholder="••••••••" data-testid="reset-confirm-input"
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white placeholder-white/25 text-sm outline-none transition-colors"
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm bg-red-900/20 border border-red-900/30 px-4 py-3">{error}</p>
            )}
            <button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-4 font-medium tracking-widest uppercase text-sm transition-colors disabled:opacity-50">
              {loading ? "Updating..." : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
